import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from '@remix-run/react';
import { Badge } from '~/components/ui/badge';
import { Input } from '~/components/ui/input';
import { Button } from '~/components/ui/button';
import { cn } from '~/lib/utils';
import type { BlogCategory, BlogTag } from '~/types/blog';
import { Search, Filter, X } from 'lucide-react';

interface BlogFilterBarProps {
  categories: BlogCategory[];
  tags: BlogTag[];
  currentCategory: string | null;
  currentTags: string[];
}

export function BlogFilterBar({
  categories,
  tags,
  currentCategory,
  currentTags
}: BlogFilterBarProps) {
  const [searchParams] = useSearchParams();
  const searchQueryFromUrl = searchParams.get('q') || '';
  const [searchQuery, setSearchQuery] = useState(searchQueryFromUrl);
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  // Update search query when URL changes
  useEffect(() => {
    setSearchQuery(searchQueryFromUrl);
  }, [searchQueryFromUrl]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      window.location.href = `/blog?q=${encodeURIComponent(searchQuery)}`;
    } else {
      // If search is empty, clear the query param
      window.location.href = '/blog';
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);

    // If the input is cleared (e.g., using the browser's clear button)
    if (value === '' && searchQueryFromUrl !== '') {
      window.location.href = '/blog';
    }
  };

  const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    if (value === '') {
      window.location.href = '/blog';
    } else {
      const params = new URLSearchParams(searchParams);
      params.delete('tag');
      params.delete('page');
      params.set('category', value);
      window.location.href = `/blog?${params.toString()}`;
    }
  };

  const handleTagToggle = (tagSlug: string) => {
    const params = new URLSearchParams(searchParams);
    params.delete('page');

    let newTags = [...currentTags];

    if (currentTags.includes(tagSlug)) {
      // Remove tag
      newTags = newTags.filter(t => t !== tagSlug);
    } else {
      // Add tag
      newTags.push(tagSlug);
    }

    if (newTags.length > 0) {
      params.set('tags', newTags.join(','));
    } else {
      params.delete('tags');
    }

    const queryString = params.toString();
    window.location.href = queryString ? `/blog?${queryString}` : '/blog';
  };

  const activeFiltersCount = (currentCategory ? 1 : 0) + currentTags.length;

  return (
    <div className="mt-8 mb-8 space-y-6">
      {/* Desktop Filter Bar */}
      <div className="hidden lg:block space-y-6">
        {/* Search Bar and Category on same row */}
        <div className="flex items-center gap-6">
            {/* Search */}
            <div className="flex-1 max-w-md">
              <form onSubmit={handleSearch} className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  type="search"
                  placeholder="Search posts..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  className="pl-10 hover:border-purple-600 transition-colors"
                />
              </form>
            </div>

            {/* Category */}
            <div className="flex items-center gap-3">
              <label htmlFor="category-select" className="text-sm font-semibold text-muted-foreground uppercase tracking-wider whitespace-nowrap">
                Category
              </label>
              <select
                id="category-select"
                value={currentCategory || ''}
                onChange={handleCategoryChange}
                className="flex h-10 w-auto min-w-[200px] rounded-md border border-input bg-background pl-3 pr-10 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 appearance-none bg-[url('data:image/svg+xml;charset=UTF-8,%3csvg xmlns=%27http://www.w3.org/2000/svg%27 width=%2724%27 height=%2724%27 viewBox=%270 0 24 24%27 fill=%27none%27 stroke=%27currentColor%27 stroke-width=%272%27 stroke-linecap=%27round%27 stroke-linejoin=%27round%27%3e%3cpath d=%27m6 9 6 6 6-6%27/%3e%3c/svg%3e')] bg-[length:1.25rem] bg-[right_0.5rem_center] bg-no-repeat hover:border-purple-600 transition-colors"
              >
                <option value="">All Posts ({categories.reduce((sum, cat) => sum + cat.count, 0)})</option>
                {categories.map(category => (
                  <option key={category.slug} value={category.slug}>
                    {category.name} ({category.count})
                  </option>
                ))}
              </select>
            </div>

            {/* Clear Filters */}
            {(currentCategory || currentTags.length > 0 || searchQueryFromUrl) && (
              <Link to="/blog">
                <Button
                  variant="outline"
                  className="whitespace-nowrap h-10 px-3 text-foreground hover:bg-transparent hover:border-purple-600 hover:text-purple-600 transition-colors"
                >
                  <X className="h-4 w-4 mr-1" />
                  Clear filters
                </Button>
              </Link>
            )}
          </div>

        {/* Tags */}
        {tags.length > 0 && (
          <div>
            <h3 className="text-sm font-semibold mb-3 text-muted-foreground uppercase tracking-wider">
              Tags
            </h3>
            <div className="flex flex-wrap gap-2">
              {tags.map(tag => {
                const isActive = currentTags.includes(tag.slug);
                return (
                  <button
                    key={tag.slug}
                    onClick={() => handleTagToggle(tag.slug)}
                    type="button"
                  >
                    <Badge
                      variant={isActive ? "default" : "secondary"}
                      className={cn(
                        "cursor-pointer transition-colors",
                        isActive ? "hover:bg-primary/80" : "hover:bg-primary/20"
                      )}
                    >
                      {tag.name} ({tag.count})
                    </Badge>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Mobile Filter Bar */}
      <div className="lg:hidden space-y-4">
        {/* Search and Filter Toggle */}
        <div className="flex items-center gap-2">
          <form onSubmit={handleSearch} className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="search"
              placeholder="Search..."
              value={searchQuery}
              onChange={handleSearchChange}
              className="pl-10 hover:border-purple-600 transition-colors"
            />
          </form>
          <Button
            variant="outline"
            size="icon"
            onClick={() => setShowMobileFilters(!showMobileFilters)}
            className={cn(
              "relative transition-colors hover:bg-transparent",
              activeFiltersCount > 0 || showMobileFilters
                ? "!border-purple-600 !text-purple-600"
                : ""
            )}
          >
            <Filter className={cn(
              "h-4 w-4",
              activeFiltersCount > 0 || showMobileFilters ? "text-purple-600" : ""
            )} />
            {activeFiltersCount > 0 && (
              <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center">
                {activeFiltersCount}
              </span>
            )}
          </Button>
        </div>

        {/* Mobile Expanded Filters */}
        {showMobileFilters && (
          <div className="space-y-4 pt-4">
            {/* Categories */}
            <div>
              <label htmlFor="category-select-mobile" className="text-sm font-semibold mb-2 text-muted-foreground block">
                Category
              </label>
              <select
                id="category-select-mobile"
                value={currentCategory || ''}
                onChange={handleCategoryChange}
                className="flex h-10 w-full rounded-md border border-input bg-background pl-3 pr-10 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 appearance-none bg-[url('data:image/svg+xml;charset=UTF-8,%3csvg xmlns=%27http://www.w3.org/2000/svg%27 width=%2724%27 height=%2724%27 viewBox=%270 0 24 24%27 fill=%27none%27 stroke=%27currentColor%27 stroke-width=%272%27 stroke-linecap=%27round%27 stroke-linejoin=%27round%27%3e%3cpath d=%27m6 9 6 6 6-6%27/%3e%3c/svg%3e')] bg-[length:1.25rem] bg-[right_0.5rem_center] bg-no-repeat hover:border-purple-600 transition-colors"
              >
                <option value="">All Posts ({categories.reduce((sum, cat) => sum + cat.count, 0)})</option>
                {categories.map(category => (
                  <option key={category.slug} value={category.slug}>
                    {category.name} ({category.count})
                  </option>
                ))}
              </select>
            </div>

            {/* Tags */}
            {tags.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold mb-2 text-muted-foreground">
                  Tags
                </h3>
                <div className="flex flex-wrap gap-2">
                  {tags.map(tag => {
                    const isActive = currentTags.includes(tag.slug);
                    return (
                      <button
                        key={tag.slug}
                        onClick={() => handleTagToggle(tag.slug)}
                        type="button"
                      >
                        <Badge
                          variant={isActive ? "default" : "secondary"}
                          className={cn(
                            "cursor-pointer transition-colors",
                            isActive ? "hover:bg-primary/80" : "hover:bg-primary/20"
                          )}
                        >
                          {tag.name} ({tag.count})
                        </Badge>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {(currentCategory || currentTags.length > 0 || searchQueryFromUrl) && (
              <Link to="/blog" className="block">
                <Button
                  variant="outline"
                  className="w-full h-10 px-3 text-foreground hover:bg-transparent hover:border-purple-600 hover:text-purple-600 transition-colors"
                >
                  <X className="h-4 w-4 mr-1" />
                  Clear all filters
                </Button>
              </Link>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
